import { defineStore } from 'pinia';
import { ref } from 'vue';

import {
  usePaxStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { CoreEventSource } from '@apps/main/new-desktop/core/models';

import { createIngredientId } from '@libs/trip-planner-interfaces';
import type { IngredientId, Pax } from '@libs/trip-planner-interfaces';
import {
  trackPaxEndChanging,
  trackPaxStartChanging,
} from '@libs/google-analytics-events';

import type {
  AirportInfo,
  FlightsListHeaderEditAirportsEvent,
} from '../components/flightsList';

export const useFlightsListModalsStore = defineStore(
  'FlightsListModalsStore',
  () => {
    const paxStore = usePaxStore();
    const statsigEventsStore = useStatsigEventsStore();

    // State
    // Change Airport Modal
    const isAirportSelectionModalOpened = ref<boolean>(false);
    const fromAirport = ref<AirportInfo>();
    const toAirport = ref<AirportInfo>();
    const modalIngredientId = ref<IngredientId>(createIngredientId(''));

    // Check airports modal
    const isCheckAirportsModalOpened = ref<boolean>(false);
    const isModalFilterOpened = ref<boolean>(false);
    const isPaxSelectorModalOpened = ref<boolean>(false);

    // Actions
    function closeFlightsListModal(): void {
      closeAirportSelectionModal();
      closeCheckAirportsModal();
      closePaxSelectorModal();
    }

    function openAirportSelectionModal(
      event: FlightsListHeaderEditAirportsEvent
    ): void {
      isAirportSelectionModalOpened.value = true;
      fromAirport.value = event.data.airports.origin;
      toAirport.value = event.data.airports.destination;
      modalIngredientId.value = event.data.ingredientId;
    }

    function closeAirportSelectionModal(): void {
      isAirportSelectionModalOpened.value = false;
      fromAirport.value = undefined;
      toAirport.value = undefined;
      modalIngredientId.value = createIngredientId('');
    }

    function openCheckAirportsModal(): void {
      isCheckAirportsModalOpened.value = true;
    }

    function closeCheckAirportsModal(): void {
      isCheckAirportsModalOpened.value = false;
    }

    function openPaxSelectorModal(): void {
      trackPaxStartChanging();
      statsigEventsStore.logCorePaxModalOpened({
        source: CoreEventSource.FlightList,
      });
      isPaxSelectorModalOpened.value = true;
    }

    function openModalFilter(): void {
      isModalFilterOpened.value = true;
    }

    function closeModalFilter(): void {
      isModalFilterOpened.value = false;
    }

    function confirmPaxSelectorModal(newPax: Pax): void {
      trackPaxEndChanging();
      paxStore.updatePax(newPax, CoreEventSource.FlightList);
      closePaxSelectorModal();
    }

    function closePaxSelectorModal(): void {
      isPaxSelectorModalOpened.value = false;
    }

    return {
      isModalFilterOpened,
      openModalFilter,
      closeModalFilter,
      isAirportSelectionModalOpened,
      toAirport,
      fromAirport,
      modalIngredientId,
      isCheckAirportsModalOpened,
      isPaxSelectorModalOpened,
      openAirportSelectionModal,
      closeAirportSelectionModal,
      openCheckAirportsModal,
      closeCheckAirportsModal,
      openPaxSelectorModal,
      confirmPaxSelectorModal,
      closePaxSelectorModal,
      closeFlightsListModal,
    };
  }
);
